import React, { useState } from 'react';
import { Grid, Paper, Typography, useMediaQuery } from '@mui/material';
import {
  isEnrollmentTerminated,
  isLegacyPresenceLifecycleProjection,
  resolveApiPresenceStatus,
  resolveEnrollmentLifecycleStatus,
} from '../../../utils/enrollmentEvidence';

const DetailPanelStatusGrid = ({
  currentRow,
  enrollmentStatusMeta,
  apiPresenceStatusMeta,
  productStatusMeta,
  terminationStatusMeta,
  programConsentMeta,
  dataConsentMeta,
  buildStatusChip,
  formatDate,
}) => {
  const statusCardSx = {
    p: 1.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    // Narrow desktop drawers can wrap a status date onto two lines. Reserve
    // that space so revealing details does not push the selections table.
    minHeight: { md: 98 },
  };
  const supportsHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [showEnrollmentDate, setShowEnrollmentDate] = useState(false);
  const [showPresenceDates, setShowPresenceDates] = useState(false);
  const [hoverEnrollmentDate, setHoverEnrollmentDate] = useState(false);
  const [hoverPresenceDates, setHoverPresenceDates] = useState(false);
  const enrollmentDateVisible = showEnrollmentDate || hoverEnrollmentDate;
  const presenceDatesVisible = showPresenceDates || hoverPresenceDates;

  const normalizeStatusKey = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;
    return str.replace(/[\s-]+/g, '_').toUpperCase();
  };

  const resolveEnrollmentDate = () => {
    const candidates = [
      currentRow?.EnrollmentDate,
      currentRow?.enrollmentDate,
      currentRow?.EnrolledDt,
      currentRow?.enrolledDt,
      currentRow?.EnrollmentDt,
      currentRow?.enrollmentDt,
      currentRow?.enrollmentDateResolved,
      currentRow?.enrollmentDateDisplay,
      currentRow?.enrollmentDateSort,
      currentRow?._original?.EnrollmentDate,
      currentRow?._original?.enrollmentDate,
      currentRow?._original?.enrolledDt,
      currentRow?._original?.enrollmentDt,
      currentRow?.sourceEnrollment?.EnrollmentDate,
      currentRow?.sourceEnrollment?.enrollmentDate,
      currentRow?.sourceEnrollment?.enrolledDt,
      currentRow?.sourceEnrollment?.enrollmentDt,
    ];
    return candidates.find((value) => value !== null && value !== undefined && value !== '');
  };

  const resolveTerminationDate = () =>
    currentRow?.TerminationDate ??
    currentRow?.terminationDate ??
    currentRow?.isTerminatedDate ??
    currentRow?.termDate ??
    currentRow?.terminationDt;

  const resolveOptOutDate = () => currentRow?.OptOutDt ?? currentRow?.optOutDt ?? currentRow?.optOutDate;

  const resolveCancelDate = () =>
    currentRow?.CancelDate ??
    currentRow?.cancelDate ??
    currentRow?.cancelDt ??
    currentRow?.cancelledAt ??
    currentRow?.syntheticCancelMetadata?.cancelEffectiveAt ??
    currentRow?.syntheticCancellationMetadata?.cancelEffectiveAt ??
    currentRow?.metadata?.cancelEffectiveAt;

  const resolveActionRequiredDate = () =>
    currentRow?.FirstEnteredAt ??
    currentRow?.firstEnteredAt ??
    currentRow?.actionRequiredFirstEnteredAt ??
    currentRow?.actionRequiredAt ??
    currentRow?._original?.firstEnteredAt ??
    currentRow?.sourceEnrollment?.firstEnteredAt;

  const resolveInProgressDate = () =>
    currentRow?.InProgressFirstEnteredAt ??
    currentRow?.inProgressFirstEnteredAt ??
    currentRow?.firstInProgressAt ??
    currentRow?.inProgressAt ??
    currentRow?._original?.inProgressFirstEnteredAt ??
    currentRow?.sourceEnrollment?.inProgressFirstEnteredAt;

  const resolvePendingProviderChangeDate = () =>
    currentRow?.DateEnteredPdact ??
    currentRow?.dateEnteredPdact ??
    currentRow?.pendingProviderChangeDate ??
    currentRow?.pendingProviderChangeAt ??
    currentRow?._original?.dateEnteredPdact ??
    currentRow?.sourceEnrollment?.dateEnteredPdact;

  const resolveMissingSince = () =>
    currentRow?.MissingSince ??
    currentRow?.missingSince ??
    currentRow?._original?.missingSince ??
    currentRow?.sourceEnrollment?.missingSince;

  const resolveMissingConfirmedDate = () =>
    currentRow?.MissingConfirmedAt ??
    currentRow?.missingConfirmedAt ??
    currentRow?._original?.missingConfirmedAt ??
    currentRow?.sourceEnrollment?.missingConfirmedAt;

  const resolveApiLastSeenDate = () =>
    currentRow?.ApiLastSeenAt ??
    currentRow?.apiLastSeenAt ??
    currentRow?._original?.apiLastSeenAt ??
    currentRow?.sourceEnrollment?.apiLastSeenAt;

  const resolveCurrentStatusFirstEnteredDate = () =>
    currentRow?.CurrentStatusFirstEnteredAt ??
    currentRow?.currentStatusFirstEnteredAt ??
    currentRow?._original?.currentStatusFirstEnteredAt ??
    currentRow?.sourceEnrollment?.currentStatusFirstEnteredAt;

  const enrollmentStatusKey = normalizeStatusKey(resolveEnrollmentLifecycleStatus(currentRow));
  const hasLegacyPresenceLifecycleProjection = isLegacyPresenceLifecycleProjection(currentRow);
  const apiPresenceStatusKey = resolveApiPresenceStatus(currentRow);
  const isTerminated = isEnrollmentTerminated(currentRow);
  const effectiveEnrollmentMeta = isTerminated
    ? terminationStatusMeta
    : hasLegacyPresenceLifecycleProjection
      ? { label: 'Lifecycle Unavailable', color: 'default' }
      : enrollmentStatusMeta;
  const presenceMetaMatchesStatus = normalizeStatusKey(apiPresenceStatusMeta?.key) === apiPresenceStatusKey;
  const fallbackPresenceMeta = apiPresenceStatusKey === 'MISSING_FROM_API'
    ? { label: 'Missing From API', color: 'warning' }
    : apiPresenceStatusKey === 'COOLING'
      ? { label: 'Cooling', color: 'warning' }
      : { label: 'Present', color: 'success' };
  const effectivePresenceMeta = apiPresenceStatusKey
    ? (presenceMetaMatchesStatus ? apiPresenceStatusMeta : fallbackPresenceMeta)
    : { label: 'Unknown', color: 'default' };
  const presenceDateRows = apiPresenceStatusKey === 'PRESENT'
    ? [{ label: 'Last Seen', value: resolveApiLastSeenDate() }]
    : apiPresenceStatusKey === 'COOLING'
      ? [{ label: 'Missing Since', value: resolveMissingSince() }]
      : apiPresenceStatusKey === 'MISSING_FROM_API'
        ? [
            { label: 'Missing Since', value: resolveMissingSince() },
            { label: 'Missing Confirmed', value: resolveMissingConfirmedDate() },
          ]
        : [{ label: 'Presence Date', value: null }];

  const enrollmentDateMeta = (() => {
    let label = 'Enrollment Date';
    let value = null;

    if (isTerminated) {
      label = 'Termination Date';
      value = resolveTerminationDate();
    } else if (hasLegacyPresenceLifecycleProjection) {
      label = 'Lifecycle Date';
      value = null;
    } else {
      switch (enrollmentStatusKey) {
        case 'ACTION_REQUIRED':
          label = 'Action Required Date';
          value = resolveActionRequiredDate() ?? resolveCurrentStatusFirstEnteredDate();
          break;
        case 'PENDING_PROVIDER_CHANGE':
          label = 'Pending Provider Change Date';
          value = resolvePendingProviderChangeDate() ?? resolveCurrentStatusFirstEnteredDate();
          break;
        case 'OPT_OUT_PENDING':
          label = 'Opt Out Date';
          value = resolveOptOutDate() ?? resolveCurrentStatusFirstEnteredDate();
          break;
        case 'OPTED_OUT':
          label = 'Opt Out Date';
          value = resolveOptOutDate() ?? resolveCurrentStatusFirstEnteredDate();
          break;
        case 'CANCELLED':
        case 'CANCELED':
          label = 'Cancel Date';
          value = resolveCancelDate() ?? resolveCurrentStatusFirstEnteredDate();
          break;
        case 'IN_PROGRESS':
          label = 'In Progress Date';
          value = resolveInProgressDate() ?? resolveCurrentStatusFirstEnteredDate();
          break;
        case 'ENROLLED':
        default:
          label = 'Enrollment Date';
          value = resolveEnrollmentDate();
          break;
      }
    }

    return { label, value };
  })();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Paper
          elevation={1}
          role="button"
          tabIndex={0}
          aria-label="Enrollment status details"
          onMouseEnter={() => {
            if (supportsHover) setHoverEnrollmentDate(true);
          }}
          onMouseLeave={() => {
            if (supportsHover) setHoverEnrollmentDate(false);
          }}
          onClick={() => {
            setShowEnrollmentDate((prev) => !prev);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setShowEnrollmentDate((prev) => !prev);
            }
          }}
          sx={{
            ...statusCardSx,
            cursor: supportsHover ? 'default' : 'pointer',
          }}
        >
          <Typography variant="caption">
            {enrollmentDateVisible ? enrollmentDateMeta.label : 'Enrollment Status'}
          </Typography>
          {enrollmentDateVisible ? (
            <>
              <Typography>{formatDate(enrollmentDateMeta.value)}</Typography>
            </>
          ) : (
            buildStatusChip(effectiveEnrollmentMeta)
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper
          elevation={1}
          role="button"
          tabIndex={0}
          aria-label="API presence details"
          onMouseEnter={() => {
            if (supportsHover) setHoverPresenceDates(true);
          }}
          onMouseLeave={() => {
            if (supportsHover) setHoverPresenceDates(false);
          }}
          onClick={() => {
            setShowPresenceDates((prev) => !prev);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setShowPresenceDates((prev) => !prev);
            }
          }}
          sx={{
            ...statusCardSx,
            cursor: supportsHover ? 'default' : 'pointer',
          }}
        >
          <Typography variant="caption">API Presence</Typography>
          {presenceDatesVisible ? (
            <>
              {presenceDateRows.map((row) => (
                <Typography key={row.label} variant="body2">
                  {row.label}: {formatDate(row.value)}
                </Typography>
              ))}
            </>
          ) : (
            buildStatusChip(effectivePresenceMeta)
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper elevation={1} sx={statusCardSx}>
          <Typography variant="caption">Product Selection Status</Typography>
          {buildStatusChip(productStatusMeta)}
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper elevation={1} sx={statusCardSx}>
          <Typography variant="caption">Program Consent Status</Typography>
          {buildStatusChip(programConsentMeta)}
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper elevation={1} sx={statusCardSx}>
          <Typography variant="caption">Data Consent Status</Typography>
          {buildStatusChip(dataConsentMeta)}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DetailPanelStatusGrid;
